/** @fileoverview Composes the scrollable assistant conversation and message list. */

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '../ai-elements/conversation';
import type { DispatchingChatMessage, FabrikUIMessage } from '../../types/chat';
import MessageList from './MessageList';
import type { ChatConversationProps } from './types';

const withDispatchingMessage = (
  messages: FabrikUIMessage[],
  dispatchingMessage?: DispatchingChatMessage
) => {
  if (!dispatchingMessage) return messages;

  const optimisticMessage: FabrikUIMessage = {
    id: `pending:${dispatchingMessage.id}`,
    role: 'user',
    parts: [
      ...(dispatchingMessage.attachments ?? []).map(({ previewUrl, ...rest }) => ({
        type: 'data-attachment' as const,
        data: { ...rest, url: previewUrl },
      })),
      ...(dispatchingMessage.text
        ? [{ type: 'text' as const, text: dispatchingMessage.text }]
        : []),
    ],
  };
  const { previousUserIds, previousAssistantIds, anchorMessageId } = dispatchingMessage;
  const isDispatchedUser = (message: FabrikUIMessage) =>
    message.role === 'user' &&
    message.id !== anchorMessageId &&
    !previousUserIds.has(message.id) &&
    message.parts
      .filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('') === dispatchingMessage.text;
  const visibleMessages = messages.filter((message) => !isDispatchedUser(message));
  const steeredResponseStarted = messages.some(
    (message) =>
      message.role === 'assistant' && !dispatchingMessage.previousAssistantIds.has(message.id)
  );
  if (dispatchingMessage.isSteering && !steeredResponseStarted) return visibleMessages;

  const anchorIndex = anchorMessageId
    ? messages.findIndex((message) => message.id === anchorMessageId)
    : -1;
  const lastUserIndex = messages.reduce(
    (lastIndex, message, index) =>
      message.role === 'user' && !isDispatchedUser(message) ? index : lastIndex,
    -1
  );
  const assistantIndex = messages.findIndex(
    (message) => message.role === 'assistant' && !previousAssistantIds.has(message.id)
  );
  const insertionIndex = dispatchingMessage.isSteering
    ? assistantIndex >= 0
      ? assistantIndex
      : messages.length
    : anchorIndex >= 0
    ? anchorIndex + 1
    : lastUserIndex >= 0
    ? lastUserIndex + 1
    : assistantIndex >= 0
    ? assistantIndex
    : messages.length;
  const visibleIndex = messages
    .slice(0, insertionIndex)
    .filter((message) => !isDispatchedUser(message)).length;

  return [
    ...visibleMessages.slice(0, visibleIndex),
    optimisticMessage,
    ...visibleMessages.slice(visibleIndex),
  ];
};

export default function ChatConversation({
  messages,
  savedMessages,
  steeringMessage,
  dispatchingMessage,
  busy,
  loading,
  hasMoreMessages,
  status,
  model,
  onRate,
  onLoadMore,
}: ChatConversationProps) {
  const steeredResponseStarted = Boolean(
    dispatchingMessage?.isSteering &&
      messages.some(
        (message) =>
          message.role === 'assistant' && !dispatchingMessage.previousAssistantIds.has(message.id)
      )
  );
  const sendingText =
    steeringMessage?.text ??
    (dispatchingMessage?.isSteering && !steeredResponseStarted
      ? dispatchingMessage.text
      : undefined);
  const hiddenActivityMessageId = dispatchingMessage?.isSteering
    ? [...messages]
        .reverse()
        .find(
          (message) =>
            message.role === 'assistant' && dispatchingMessage.previousAssistantIds.has(message.id)
        )?.id
    : sendingText
    ? [...messages]
        .reverse()
        .find(
          (message) => message.role === 'assistant' && !(busy && messages.at(-1)?.id === message.id)
        )?.id
    : undefined;

  return (
    <Conversation className="min-h-0 flex-1">
      <ConversationContent className="mx-auto w-full max-w-5xl gap-3 px-7 pb-8 pt-11 @max-5xl:mx-0 @max-5xl:max-w-none @max-5xl:px-3 @max-5xl:py-4">
        <MessageList
          messages={withDispatchingMessage(messages, dispatchingMessage)}
          savedMessages={savedMessages}
          busy={busy}
          loading={loading}
          hasMoreMessages={hasMoreMessages}
          status={status}
          model={model}
          onRate={onRate}
          onLoadMore={onLoadMore}
          sendingText={sendingText}
          hideActivityMessageId={hiddenActivityMessageId}
        />
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}
